import { zodResolver } from "@hookform/resolvers/zod"
import { useAtomValue } from "jotai"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { type ChangeEmailRequest, useChangeEmail } from "@/api/client/auth"
import { changeEmailRequestSchema } from "@/api/client/auth/schemas"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { cn } from "@/lib/utils"
import { sessionUserAtom } from "@/store/session"

type TabItem = {
  label: string
  path: string
  isActive: boolean
}

function ProfileEditTabs({ tabs }: { tabs: TabItem[] }) {
  return (
    <div className="bg-slate-100 rounded-md p-1 flex">
      {tabs.map((tab) => (
        <Link
          key={tab.path}
          to={tab.path}
          className={cn(
            "flex-1 text-center py-1.5 px-3 text-sm font-semibold rounded-sm transition-all",
            tab.isActive ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-700"
          )}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  )
}

export function ChangeEmailPage() {
  const navigate = useNavigate()
  const sessionUser = useAtomValue(sessionUserAtom)

  const form = useForm<ChangeEmailRequest>({
    resolver: zodResolver(changeEmailRequestSchema),
    defaultValues: {
      newEmail: "",
    },
  })

  const { mutateAsync: changeEmail, isPending } = useChangeEmail({
    onSuccess: () => {
      toast.success("認証メールを送信しました", {
        description: "新しいメールアドレスに届いた認証リンクをクリックして変更を完了してください",
      })
      navigate("/profile")
    },
    onError: () => {
      toast.error("メールアドレスの変更に失敗しました")
    },
  })

  const tabs: TabItem[] = [
    { label: "基本情報", path: "/profile/edit", isActive: false },
    { label: "メールアドレス", path: "/profile/edit/change-email", isActive: true },
    { label: "パスワード", path: "/profile/edit/change-password", isActive: false },
  ]

  const handleSubmit = async (values: ChangeEmailRequest) => {
    await changeEmail(values)
  }

  const handleCancel = () => {
    navigate("/profile")
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
          <ArrowLeft className="size-4" />
          戻る
        </Button>
        <h1 className="text-base font-semibold text-primary">プロフィール設定</h1>
      </div>

      {/* Tabs */}
      <ProfileEditTabs tabs={tabs} />

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <Card>
            <CardContent className="pt-5 space-y-4">
              {/* Title Section */}
              <div className="pb-1">
                <h2 className="text-xl font-bold text-slate-600">メールアドレス変更</h2>
                <p className="text-xs text-slate-500 mt-1">
                  アカウントのメールアドレスを変更します
                </p>
              </div>

              {/* Current Email (Display Only) */}
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-slate-600">現在のメールアドレス</p>
                <p className="text-sm text-slate-600">{sessionUser?.email || "未設定"}</p>
              </div>

              {/* New Email */}
              <FormField
                control={form.control}
                name="newEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-slate-600">
                      新しいメールアドレス
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="example@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-slate-500">
                      ご変更には、新しいメールアドレスでの認証が必要です
                    </p>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-2.5 justify-end">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
              キャンセル
            </Button>
            <Button type="submit" loading={isPending}>
              認証する
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
